import { HotelBookingService } from 'src/app/services/hotel-booking/hotel-booking.service';
import { Component, Input, OnInit } from '@angular/core';
import { catchError, finalize, forkJoin, Observable, of, tap } from 'rxjs';
import { EventService } from 'src/app/services/events/event.service';
import { environment } from 'src/environments/environment';
import { NavController } from '@ionic/angular';
import { ThemeService } from 'src/app/services/client/theme/theme.service';

@Component({
  selector: 'app-ancillary-services',
  templateUrl: './ancillary-services.component.html',
  styleUrls: ['./ancillary-services.component.scss'],
})
export class AncillaryServicesComponent implements OnInit {
  @Input() itinerary?: any;

  public originDestinationPairs: any;
  public uniqueAirportCodes: any;
  public loungeList: any;
  public hotelList: any = [];
  public loungeCarouselId = 127;
  public imagePlaceholder = 'assets/images/placeholder.png';
  hotelSearchRequest: any;
  entityId: any;
  entityLabel: any;
  public loading = true;
  public loungeLoading = true;

  slideOpts = {
    initialSlide: 0,
    speed: 400,
    loop: true,
    autoplay: {
      delay: 3000,
    },
    slidesPerView: 1,
  };
  constructor(
    private eventService: EventService,
    private hotelBookingService: HotelBookingService,
    private navController: NavController,
    private themeService: ThemeService
  ) {}

  ngOnInit() {
    this.getflightRoutes();
    this.getAncillaryServices();
  }

  ionViewDidEnter() {}

  navigateToHotelDetail(hotelDetail) {
    this.navController.navigateForward([
      '/hotel/details/',
      hotelDetail?.data?.detail?.hotelCode,
    ]);
  }

  getflightRoutes(): string[] {
    const segments = this.itinerary?.Segments ?? [];
    const routeInfoSet = new Set<string>();

    segments.forEach((segmentGroup) => {
      segmentGroup.forEach((segment) => {
        const originCode = segment.Origin?.AirportCode;
        const originTerminal = segment.departureTerminal || '';

        const destinationCode = segment.Destination?.AirportCode;
        const destinationTerminal = segment.arrivalTerminal || '';

        if (originCode) {
          routeInfoSet.add(`${originCode} - Departure - ${originTerminal}`);
        }

        if (destinationCode) {
          routeInfoSet.add(
            `${destinationCode} - Arrival - ${destinationTerminal}`
          );
        }
      });
    });

    const routeInfoArray = Array.from(routeInfoSet);
    this.uniqueAirportCodes = routeInfoArray;

    return routeInfoArray;
  }

  findDestinationByAirportCode(searchCode: string): any | null {
    const segments = this.itinerary?.Segments ?? [];

    for (const group of segments) {
      for (const segment of group) {
        const dest = segment?.Destination;
        if (dest?.AirportCode?.toUpperCase() === searchCode.toUpperCase()) {
          return dest;
        }
      }
    }

    return null; // Not found
  }

  async fetchHotelData() {
    this.loading = true;
    const offset = 5;

    this.hotelBookingService
      .getHotelDetails(this.hotelSearchRequest, offset)
      .pipe(finalize(() => (this.loading = false)))
      .subscribe({
        next: (response) => {
          if (response?.request?.requestId) {
            this.hotelSearchRequest.requestId = response.request.requestId;
            this.hotelBookingService.saveSearchQuery(this.hotelSearchRequest);
          }
          this.hotelList =
            response?.content?.length > 0 ? [...response.content] : [];
        },
        error: () => {
          this.hotelList = [];
        },
      });
  }

  async getAncillaryServices() {
    this.loungeLoading = true;
    this.loungeList = {}; // Initialize/reset the lounge list

    const loungeRequests: Observable<any>[] = [];

    this.uniqueAirportCodes.forEach((element) => {
      let location = '';
      const dataArray = element.split(' - ');

      if (dataArray[2]) {
        location =
          'terminal ' + dataArray[2] + ' - ' + dataArray[1].toLowerCase();
      }

      // Initialize with empty array for safe template access
      this.loungeList[element] = [];

      if (location) {
        const loungeRequest$ = this.eventService
          .getLounges(0, this.loungeCarouselId, dataArray[0], location)
          .pipe(
            tap((response) => {
              this.loungeList[element] =
                response?.selectedTabItems?.[0]?.services || [];
            }),
            // Isolate failures so one bad request doesn't cancel the rest
            catchError(() => {
              this.loungeList[element] = [];
              return of(null);
            })
          );

        loungeRequests.push(loungeRequest$);
      }
    });

    // Wait for all lounge API calls to complete.
    // forkJoin([]) never emits, so guard against an empty request list.
    if (loungeRequests.length === 0) {
      this.loungeLoading = false;
    } else {
      forkJoin(loungeRequests)
        .pipe(finalize(() => (this.loungeLoading = false)))
        .subscribe();
    }

    // ---------------- Hotel logic below ----------------

    const destObj = this.findDestinationByAirportCode(
      this.itinerary?.Destination?.toUpperCase()
    );

    this.loading = true;
    this.hotelBookingService
      .getCityHotelSuggestions(destObj?.CityName?.toLowerCase(), 0)
      .subscribe({
        next: (response) => {
          if (!response?.content?.length) {
            // No city suggestions: nothing to fetch, stop the skeleton.
            this.hotelList = [];
            this.loading = false;
            return;
          }
          this.entityId = response.content[0].entityId;
          this.entityLabel = `${response?.content?.[0]?.title ?? ''}${ response?.content?.[0]?.country ? `, ${response.content[0].country}` : ''}`;

          const formattedCheckinDate = this.itinerary?.TravelDate
            ? new Date(this.itinerary?.TravelDate).toISOString().split('T')[0]
            : new Date();
          const checkoutDate = this.itinerary?.TravelDate
            ? new Date(this.itinerary?.TravelDate)
            : new Date();
          checkoutDate.setDate(checkoutDate.getDate() + 1);
          const formattedCheckoutDate = checkoutDate
            .toISOString()
            .split('T')[0];

          this.hotelSearchRequest = {
            entityId: this.entityId,
            entityLabel: this.entityLabel,
            checkin: formattedCheckinDate,
            checkout: formattedCheckoutDate,
            nationality: this.themeService.getDefaultNationality?.iso2,
            pid: environment.pid,
            rt: 'compact',
            type: 'TBOCITY',
            rooms: [
              {
                adults: 1,
                children: 0,
                childrenAges: [],
              },
            ],
            fq: {
              df_long_star_rating: [],
            },
          };
          this.fetchHotelData();
        },
        error: () => {
          // Suggestions failed: clear hotels and stop the skeleton.
          this.hotelList = [];
          this.loading = false;
        },
      });
  }

  loungeListKeys(): string[] {
    return Object.keys(this.loungeList);
  }

  handleRedirect(item) {
    if (item?.type == 'lounges') {
      this.navController.navigateForward([
        '/lounge/' +
          item?.serviceid +
          '/' +
          item?.selectedLocation?.locationInfo?.locationName,
      ]);
    }
  }

  navigate() {
    this.navController.navigateForward(['/meet-and-greet/']);
  }
}
